'use client';

import { c as _c } from "react/compiler-runtime";
import type { InitialTableState } from '@tanstack/react-table';
import usePagination from '@mui/material/usePagination';
import NumberFlow from '@number-flow/react';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, getExpandedRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import clsx from 'clsx';
import { format } from 'date-fns';
import { pipe } from 'effect';
import { identity, uniqBy } from 'es-toolkit';
import { useDebouncedValue } from 'foxact/use-debounced-value';
import { produce } from 'immer';
import { atom, useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import ms from 'ms';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Fragment, startTransition, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useImmer } from 'use-immer';
import type { Download } from '@/app/[locale]/subtitles/_api/os';
import type { tmdb } from '@sub-vocab/utils/types';
import { $osApi, openSubtitlesDownloadAtom, openSubtitlesTextAtom } from '@/app/[locale]/subtitles/_api/os';
import { $api } from '@/app/[locale]/subtitles/_api/tmdb';
import { fileIdsAtom, mediaSubtitleFamily, osLanguageAtom } from '@/app/[locale]/subtitles/_atoms';
import { MediaDetails } from '@/app/[locale]/subtitles/_components/media-details';
import { OpensubtitlesAuthentication } from '@/app/[locale]/subtitles/_components/opensubtitles-authentication';
import { fileInfoAtom, sourceTextAtom } from '@/atoms/vocabulary';
import { SortIcon } from '@/components/my-icon/sort-icon';
import { TableGoToLastPage } from '@/components/my-table/go-to-last-page';
import { TablePagination } from '@/components/my-table/pagination';
import { TablePaginationSizeSelect } from '@/components/my-table/pagination-size-select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Div } from '@/components/ui/html-elements';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { HeaderTitle, TableDataCell, TableHeader, TableHeaderCell, TableHeaderCellRender, TableRow } from '@/components/ui/table-element';
import { filterFn } from '@/lib/table-utils';
import { useI18n } from '@/locales/client';
import { useClone } from '@sub-vocab/utils/hooks';
import { findClosest, tap } from '@sub-vocab/utils/lib';
const mediaSearchAtom = atomWithStorage('mediaSearchAtom', '');
const popularityNumberFormat = new Intl.NumberFormat('en', {
  maximumFractionDigits: 1,
  minimumFractionDigits: 1
});
const voteNumberFormat = new Intl.NumberFormat('en', {
  maximumFractionDigits: 1,
  minimumFractionDigits: 1
});
const voteCountFormat = new Intl.NumberFormat('en', {
  notation: 'compact',
  compactDisplay: 'short'
});
const listFormatter = new Intl.ListFormat('en', {
  style: 'long',
  type: 'conjunction'
});
type TableData = NonNullable<tmdb.paths['/3/search/multi']['get']['responses'][200]['content']['application/json']['results']>[number];

/// keep-unique
const PAGE_SIZES = [5, 10, 20] as const;
const initialTableStateAtom = atom(identity<InitialTableState>({
  sorting: [{
    id: 'popularity',
    desc: true
  }],
  pagination: {
    pageSize: findClosest(100, PAGE_SIZES),
    pageIndex: 0
  }
}));
function useColumns(rootRef) {
  const $ = _c(14);
  useI18n();
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== rootRef?.current) {
    const columnHelper = createColumnHelper();
    t0 = columnHelper.accessor(_temp, {
      id: "action",
      header: t6 => {
        const {
          header
        } = t6;
        const isSorted = header.column.getIsSorted();
        return <TableHeaderCell header={header} className="w-[.1%] active:signal/active [&:active+th]:signal/active"><Div className="min-w-10 justify-center gap-2 select-none signal/active:bg-background-active" onClick={e => {
            header.column.getToggleSortingHandler()?.(e);
            requestAnimationFrame(() => rootRef?.current?.scrollTo({
              top: 0
            }));
          }}><div className="flex"><SortIcon isSorted={isSorted} /></div></Div></TableHeaderCell>;
      },
      cell: _temp3
    });
    t1 = columnHelper.accessor(_temp4, {
      id: "upload_date",
      header: t7 => {
        const {
          header: header_0
        } = t7;
        const isSorted_0 = header_0.column.getIsSorted();
        return <TableHeaderCell header={header_0} className="w-[.1%] active:bg-background-active active:signal/active [&:active+th]:signal/active"><Div className="group gap-2 pr-1 select-none" onClick={e_1 => {
            header_0.column.getToggleSortingHandler()?.(e_1);
            requestAnimationFrame(() => rootRef?.current?.scrollTo({
              top: 0
            }));
          }}><Separator orientation="vertical" className="h-5 signal/active:h-full" /><HeaderTitle title="Year" isSorted={isSorted_0} className="*:data-title:text-left" /></Div></TableHeaderCell>;
      },
      cell: _temp5
    });
    t2 = columnHelper.accessor(_temp6, {
      id: "media_type",
      header: t8 => {
        const {
          header: header_1
        } = t8;
        const isSorted_1 = header_1.column.getIsSorted();
        return <TableHeaderCell header={header_1} className="w-[.1%] active:bg-background-active active:signal/active [&:active+th]:signal/active"><Div className="group gap-2 pr-1 select-none" onClick={e_2 => {
            header_1.column.getToggleSortingHandler()?.(e_2);
            requestAnimationFrame(() => rootRef?.current?.scrollTo({
              top: 0
            }));
          }}><Separator orientation="vertical" className="h-5 signal/active:h-full" /><HeaderTitle title="Media" isSorted={isSorted_1} className="*:data-title:text-right" /></Div></TableHeaderCell>;
      },
      cell: _temp7
    });
    t3 = columnHelper.accessor(_temp8, {
      id: "movie_name",
      filterFn,
      header: t9 => {
        const {
          header: header_2
        } = t9;
        const isSorted_2 = header_2.column.getIsSorted();
        return <TableHeaderCell header={header_2} className="active:bg-background-active active:signal/active [&:active+th]:signal/active"><Div className="group gap-2 pr-1" onClick={e_3 => {
            header_2.column.getToggleSortingHandler()?.(e_3);
            requestAnimationFrame(() => rootRef?.current?.scrollTo({
              top: 0
            }));
          }}><Separator orientation="vertical" className="h-5 signal/active:h-full" /><HeaderTitle title="Name" isSorted={isSorted_2} className="*:data-title:text-left" /></Div></TableHeaderCell>;
      },
      cell: _temp10
    });
    t4 = columnHelper.accessor(_temp11, {
      id: "vote_average",
      header: t10 => {
        const {
          header: header_3
        } = t10;
        const isSorted_3 = header_3.column.getIsSorted();
        return <TableHeaderCell header={header_3} className="w-[.1%] active:bg-background-active active:signal/active [&:active+th]:signal/active"><Div className="group gap-2 pr-1 select-none" onClick={e_4 => {
            header_3.column.getToggleSortingHandler()?.(e_4);
            requestAnimationFrame(() => rootRef?.current?.scrollTo({
              top: 0
            }));
          }}><Separator orientation="vertical" className="h-5 signal/active:h-full" /><HeaderTitle title="Rating" isSorted={isSorted_3} className="*:data-title:text-left" /></Div></TableHeaderCell>;
      },
      cell: _temp12
    });
    t5 = columnHelper.accessor(_temp13, {
      id: "popularity",
      header: t11 => {
        const {
          header: header_4
        } = t11;
        const isSorted_4 = header_4.column.getIsSorted();
        return <TableHeaderCell header={header_4} className="w-[.1%] active:bg-background-active active:signal/active [&:active+th]:signal/active"><Div className="group gap-2 pr-1 select-none" onClick={e_5 => {
            header_4.column.getToggleSortingHandler()?.(e_5);
            requestAnimationFrame(() => rootRef?.current?.scrollTo({
              top: 0
            }));
          }}><Separator orientation="vertical" className="h-5 signal/active:h-full" /><HeaderTitle title="Popularity" isSorted={isSorted_4} className="*:data-title:text-right" /></Div></TableHeaderCell>;
      },
      cell: _temp14
    });
    $[0] = rootRef?.current;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t0 = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t0 || $[8] !== t1 || $[9] !== t2 || $[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = [t0, t1, t2, t3, t4, t5];
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function _temp14(t0) {
  const {
    cell: cell_4,
    getValue: getValue_3
  } = t0;
  const value_3 = getValue_3();
  return <TableDataCell cell={cell_4}><Div className="justify-end pr-5 pl-0.5 tracking-[.03em] tabular-nums"><span>{popularityNumberFormat.format(value_3)}</span></Div></TableDataCell>;
}
function _temp13(row_6) {
  return row_6.popularity || 0;
}
function _temp12(t0) {
  const {
    cell: cell_3,
    row: row_5,
    getValue: getValue_2
  } = t0;
  const value_2 = getValue_2();
  const voteCount = row_5.original.vote_count;
  return <TableDataCell cell={cell_3}>{voteCount >= 1 ? <Div className="flex flex-nowrap gap-1 px-1 tracking-[.03em] tabular-nums"><div className="flex items-center gap-1.5"><svg className="icon-[clarity--star-solid] text-neutral-800 dark:text-neutral-100" /><span className="font-semibold">{voteNumberFormat.format(value_2)}</span></div><span>{`(${voteCountFormat.format(voteCount)})`}</span></Div> : <Fragment />}</TableDataCell>;
}
function _temp11(row_4) {
  return row_4.vote_average;
}
function _temp10(t0) {
  const {
    cell: cell_2,
    getValue: getValue_1
  } = t0;
  const value_1 = getValue_1();
  return <TableDataCell cell={cell_2}><Div className="cursor-text py-1 pr-px pl-2.5 tracking-[.04em] select-text" onClick={_temp9}><span>{value_1}</span></Div></TableDataCell>;
}
function _temp9(ev) {
  return ev.stopPropagation();
}
function _temp8(row_3) {
  return row_3.title ?? row_3.original_title ?? row_3.original_name;
}
function _temp7(t0) {
  const {
    cell: cell_1,
    getValue: getValue_0
  } = t0;
  const value_0 = getValue_0();
  return <TableDataCell cell={cell_1}><Div className="justify-center pr-px pl-0.5 capitalize data-[value=tv]:tracking-[.05em] data-[value=tv]:uppercase" data-value={value_0}>{value_0}</Div></TableDataCell>;
}
function _temp6(row_2) {
  return row_2.media_type;
}
function _temp5(t0) {
  const {
    cell: cell_0,
    getValue
  } = t0;
  const value = getValue();
  return <TableDataCell cell={cell_0}><Div className="justify-center pr-px pl-0.5 tracking-[.03em] tabular-nums">{value ? format(value, "yyyy") : null}</Div></TableDataCell>;
}
function _temp4(row_1) {
  return row_1.release_date ?? row_1.first_air_date;
}
function _temp3(t0) {
  const {
    row: row_0,
    cell,
    onExpandedChange
  } = t0;
  const isExpanded = row_0.getIsExpanded();
  return <TableDataCell cell={cell}><Div className="pr-px pl-0.5 text-zinc-400">{row_0.getCanExpand() ? <div className="flex h-full grow cursor-pointer items-center justify-center select-none" onClick={() => {
        onExpandedChange?.(isExpanded);
      }}>{"\u200B"}<svg className={clsx("icon-[lucide--chevron-right] size-3.5 text-zinc-400 transition-transform dark:text-zinc-500", isExpanded ? "rotate-90" : "")} /></div> : <div className="w-full justify-end px-3"><Checkbox onClick={_temp2} /></div>}</Div></TableDataCell>;
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp(row) {
  return row.id;
}
export default function Subtitles() {
  const $ = _c(146);
  const store = useStore();
  const {
    isPending: isDownloadPending,
    mutateAsync: downloadText
  } = useAtomValue(openSubtitlesTextAtom);
  const {
    isPending: isFileDownloadPending,
    mutateAsync: downloadFile
  } = useAtomValue(openSubtitlesDownloadAtom);
  const [query, setQuery] = useAtom(mediaSearchAtom);
  const debouncedQuery = useDebouncedValue(query, 500);
  const rootRef = useRef(null);
  const columns = useColumns(rootRef);
  const setFileInfo = useSetAtom(fileInfoAtom);
  const setSourceText = useSetAtom(sourceTextAtom);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [subtitleDownloadProgress, setSubtitleDownloadProgress] = useImmer(t0 as Download['Body'][]);
  const queryEnabled = Boolean(debouncedQuery);
  let t1;
  if ($[1] !== debouncedQuery || $[2] !== queryEnabled) {
    t1 = $api.queryOptions("get", "/3/search/multi", {
      params: {
        query: {
          query: debouncedQuery
        }
      }
    }, {
      enabled: queryEnabled,
      placeholderData: _temp15
    });
    $[1] = debouncedQuery;
    $[2] = queryEnabled;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data: multiData,
    isFetching: isSearchLoading
  } = useQuery(t1);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = $osApi.queryOptions("get", "/infos/languages", {}, {
      gcTime: ms("1 weeks"),
      staleTime: ms("1 weeks"),
      select: _temp16,
      placeholderData: _temp17
    });
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: t3
  } = useQuery(t2);
  let t4;
  if ($[5] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const languages = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      language_code: "all",
      language_name: "All Languages"
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== languages) {
    t6 = [t5, ...uniqBy([{
      language_code: "en",
      language_name: "English"
    }, ...languages], _temp18).sort(_temp19)];
    $[8] = languages;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const languageOptions = t6;
  let t7;
  if ($[10] !== languageOptions) {
    t7 = languageOptions.map(_temp20);
    $[10] = languageOptions;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const languageItems = t7;
  const tvAndMovieResults = queryEnabled ? (multiData?.results ?? []).filter(_temp21) : [];
  const table = useClone(useReactTable({
    data: tvAndMovieResults,
    columns,
    initialState: store.get(initialTableStateAtom),
    autoResetPageIndex: false,
    getRowId: _temp22,
    getRowCanExpand: _temp23,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onStateChange: updater => {
      store.set(initialTableStateAtom, typeof updater === "function" ? updater(tableState) : updater);
    }
  }));
  let t8;
  if ($[12] !== table) {
    t8 = table.getFilteredRowModel();
    $[12] = table;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const rowsFiltered = t8.rows;
  const router = useRouter();
  const [fileIds] = useAtom(fileIdsAtom);
  let t9;
  if ($[14] !== downloadText || $[15] !== router || $[16] !== setFileInfo || $[17] !== setSourceText || $[18] !== setSubtitleDownloadProgress) {
    t9 = async function getText(fileIds_0) {
      pipe(await Promise.all(fileIds_0.map(id => {
        const file = {
          file_id: id
        };
        return downloadText(file).then(tap(() => {
          setSubtitleDownloadProgress(prev_1 => {
            prev_1.push(file);
          });
        }));
      })), tap(x => {
        setFileInfo(listFormatter.format(x.map(_temp24)));
      }), tap(x_0 => {
        setSourceText(v => ({
          value: x_0.map(_temp25).join("\n"),
          epoch: v.epoch + 1
        }));
      }));
      router.push("/");
      startTransition(() => {
        setSubtitleDownloadProgress([]);
      });
    };
    $[14] = downloadText;
    $[15] = router;
    $[16] = setFileInfo;
    $[17] = setSourceText;
    $[18] = setSubtitleDownloadProgress;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const getText = t9;
  let t10;
  if ($[20] !== downloadFile || $[21] !== setSubtitleDownloadProgress) {
    t10 = async function downloadFiles(fileIds_1) {
      await Promise.all(fileIds_1.map(async id_0 => {
        const file_0 = {
          file_id: id_0
        };
        await downloadFile(file_0);
        setSubtitleDownloadProgress(prev_2 => {
          prev_2.push(file_0);
        });
      }));
      setSubtitleDownloadProgress([]);
    };
    $[20] = downloadFile;
    $[21] = setSubtitleDownloadProgress;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const downloadFiles = t10;
  let t11;
  if ($[23] !== store) {
    t11 = function clearSelection() {
      Array.from(mediaSubtitleFamily.getParams(), mediaSubtitleFamily).forEach(a_0 => {
        store.set(a_0, produce(_temp26));
      });
    };
    $[23] = store;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const clearSelection = t11;
  const tableState = table.getState();
  let t12;
  if ($[25] !== table) {
    t12 = table.getPageCount();
    $[25] = table;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = tableState.pagination.pageIndex + 1;
  let t14;
  if ($[27] !== t12 || $[28] !== t13) {
    t14 = {
      count: t12,
      page: t13
    };
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const {
    items
  } = usePagination(t14);
  const t = useI18n();
  const inputRef = useRef(null);
  let t15;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = e => {
      if (document.activeElement === inputRef.current) {
        return;
      }
      e.preventDefault();
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    };
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  useHotkeys("meta+f", t15);
  const isLoading = isDownloadPending || isFileDownloadPending;
  const [language_1, setLanguage] = useAtom(osLanguageAtom);
  let t16;
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Button variant="secondary" className="h-8 gap-0.5 pr-3 pl-2.5 text-xs" asChild={true}><Link href="/"><svg className="icon-[mi--chevron-left] size-4.5" />Home</Link></Button>;
    t17 = <div className="grow" />;
    $[31] = t16;
    $[32] = t17;
  } else {
    t16 = $[31];
    t17 = $[32];
  }
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <PopoverTrigger render={<Button variant="ghost" className="h-8 gap-1.5 px-3"><svg className="icon-[oui--token-key] min-w-[1em]" /><span className="hidden md:block">OpenSubtitles</span></Button>} />;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Popover>{t18}<PopoverContent className="w-72 [--sq-r:1.75rem]"><OpensubtitlesAuthentication /></PopoverContent></Popover>;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== fileIds || $[36] !== getText) {
    t20 = () => {
      getText(fileIds);
    };
    $[35] = fileIds;
    $[36] = getText;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  const t21 = fileIds.length === 0 || isLoading;
  let t22;
  let t23;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <svg className="icon-[f7--captions-bubble-fill] min-w-[1em]" />;
    t23 = <span className="hidden md:block">View Text</span>;
    $[38] = t22;
    $[39] = t23;
  } else {
    t22 = $[38];
    t23 = $[39];
  }
  let t24;
  if ($[40] !== t20 || $[41] !== t21) {
    t24 = <Button className="h-8 gap-1.5 px-3 tracking-[.03em] tabular-nums" onClick={t20} disabled={t21}>{t22}{t23}</Button>;
    $[40] = t20;
    $[41] = t21;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== downloadFiles || $[44] !== fileIds) {
    t25 = () => {
      downloadFiles(fileIds);
    };
    $[43] = downloadFiles;
    $[44] = fileIds;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  const t26 = fileIds.length === 0 || isLoading;
  let t27;
  let t28;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <svg className="icon-[f7--arrow-down-circle-fill] min-w-[1em]" />;
    t28 = <span className="hidden md:block">Download</span>;
    $[46] = t27;
    $[47] = t28;
  } else {
    t27 = $[46];
    t28 = $[47];
  }
  let t29;
  if ($[48] !== t25 || $[49] !== t26) {
    t29 = <Button className="h-8 gap-1.5 px-3 tracking-[.03em] tabular-nums" onClick={t25} disabled={t26}>{t27}{t28}</Button>;
    $[48] = t25;
    $[49] = t26;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] !== t24 || $[52] !== t29) {
    t30 = <div className="pb-3"><div className="flex items-center gap-2">{t16}{t17}{t19}{t24}{t29}</div></div>;
    $[51] = t24;
    $[52] = t29;
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  let t31;
  if ($[54] !== setQuery) {
    t31 = e_0 => {
      setQuery(e_0.target.value);
    };
    $[54] = setQuery;
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  let t32;
  if ($[56] !== query || $[57] !== t31) {
    t32 = <div><Input ref={inputRef} type="text" placeholder="Search" value={query} onChange={t31} className="h-full text-base [--sq-r:.75rem] md:text-sm" /></div>;
    $[56] = query;
    $[57] = t31;
    $[58] = t32;
  } else {
    t32 = $[58];
  }
  let t33;
  if ($[59] !== setLanguage) {
    t33 = e_1 => {
      setLanguage(e_1);
    };
    $[59] = setLanguage;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <SelectTrigger className="h-full! w-[unset] gap-0 px-2 py-0 text-xs tracking-[.03em] tabular-nums"><SelectValue placeholder="Select" /></SelectTrigger>;
    $[61] = t34;
  } else {
    t34 = $[61];
  }
  let t35;
  if ($[62] !== languageOptions) {
    t35 = <SelectContent position="item-aligned" className="tracking-[.03em] tabular-nums">{languageOptions.map(_temp27)}</SelectContent>;
    $[62] = languageOptions;
    $[63] = t35;
  } else {
    t35 = $[63];
  }
  let t36;
  if ($[64] !== languageItems || $[65] !== language_1 || $[66] !== t33 || $[67] !== t35) {
    t36 = <Select items={languageItems} value={language_1} onValueChange={t33}>{t34}{t35}</Select>;
    $[64] = languageItems;
    $[65] = language_1;
    $[66] = t33;
    $[67] = t35;
    $[68] = t36;
  } else {
    t36 = $[68];
  }
  let t37;
  if ($[69] !== isSearchLoading) {
    t37 = isSearchLoading ? <svg className="icon-[lucide--loader-2] animate-spin" /> : null;
    $[69] = isSearchLoading;
    $[70] = t37;
  } else {
    t37 = $[70];
  }
  let t38;
  if ($[71] !== t37) {
    t38 = <div className="flex aspect-square h-full grow items-center justify-start pl-1.5">{t37}</div>;
    $[71] = t37;
    $[72] = t38;
  } else {
    t38 = $[72];
  }
  const t39 = fileIds.length === 0 ? "hidden" : "";
  let t40;
  if ($[73] !== t39) {
    t40 = clsx(t39, "h-8 gap-1.5 px-3");
    $[73] = t39;
    $[74] = t40;
  } else {
    t40 = $[74];
  }
  const t41 = fileIds.length === 0 || isLoading;
  let t42;
  let t43;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <svg className="icon-[f7--multiply-circle] min-w-[1em]" />;
    t43 = <span className="hidden md:block">Clear</span>;
    $[75] = t42;
    $[76] = t43;
  } else {
    t42 = $[75];
    t43 = $[76];
  }
  let t44;
  if ($[77] !== clearSelection || $[78] !== t40 || $[79] !== t41) {
    t44 = <Button className={t40} variant="ghost" disabled={t41} onClick={clearSelection}>{t42}{t43}</Button>;
    $[77] = clearSelection;
    $[78] = t40;
    $[79] = t41;
    $[80] = t44;
  } else {
    t44 = $[80];
  }
  let t45;
  if ($[81] !== isLoading) {
    t45 = isLoading ? <svg className="icon-[lucide--loader-2] min-w-[1em] animate-spin" /> : null;
    $[81] = isLoading;
    $[82] = t45;
  } else {
    t45 = $[82];
  }
  let t46;
  if ($[83] !== fileIds.length || $[84] !== isLoading || $[85] !== subtitleDownloadProgress) {
    t46 = isLoading ? <div className="flex items-center gap-1.5"><span><NumberFlow value={subtitleDownloadProgress.length} />{" / "}<NumberFlow value={fileIds.length} /></span></div> : <NumberFlow value={fileIds.length} className={clsx(fileIds.length === 0 ? "hidden" : "")} isolate={true} />;
    $[83] = fileIds.length;
    $[84] = isLoading;
    $[85] = subtitleDownloadProgress;
    $[86] = t46;
  } else {
    t46 = $[86];
  }
  const t47 = fileIds.length === 0 ? "hidden" : "";
  let t48;
  if ($[87] !== t47) {
    t48 = clsx(t47);
    $[87] = t47;
    $[88] = t48;
  } else {
    t48 = $[88];
  }
  const t49 = fileIds.length === 1 ? "item" : "items";
  let t50;
  if ($[89] !== t48 || $[90] !== t49) {
    t50 = <span className={t48}>{t49}</span>;
    $[89] = t48;
    $[90] = t49;
    $[91] = t50;
  } else {
    t50 = $[91];
  }
  let t51;
  if ($[92] !== t45 || $[93] !== t46 || $[94] !== t50) {
    t51 = <div className="flex items-center gap-1.5 pr-1.5 text-sm">{t45}{t46}{t50}</div>;
    $[92] = t45;
    $[93] = t46;
    $[94] = t50;
    $[95] = t51;
  } else {
    t51 = $[95];
  }
  let t52;
  if ($[96] !== t32 || $[97] !== t36 || $[98] !== t38 || $[99] !== t44 || $[100] !== t51) {
    t52 = <div><div className="flex h-12 gap-2 p-2">{t32}{t36}{t38}{t44}{t51}</div></div>;
    $[96] = t32;
    $[97] = t36;
    $[98] = t38;
    $[99] = t44;
    $[100] = t51;
    $[101] = t52;
  } else {
    t52 = $[101];
  }
  let t53;
  if ($[102] !== table) {
    t53 = table.getHeaderGroups().map(_temp29);
    $[102] = table;
    $[103] = t53;
  } else {
    t53 = $[103];
  }
  let t54;
  if ($[104] !== t53) {
    t54 = <TableHeader>{t53}</TableHeader>;
    $[104] = t53;
    $[105] = t54;
  } else {
    t54 = $[105];
  }
  let t55;
  if ($[106] !== table) {
    let t56;
    if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
      t56 = (row_0, index) => <TableRow key={row_0.id} row={row_0} root={rootRef} index={index + 1}><MediaDetails id={row_0.original.id} media_type={row_0.original.media_type} /></TableRow>;
      $[108] = t56;
    } else {
      t56 = $[108];
    }
    t55 = table.getRowModel().rows.map(t56);
    $[106] = table;
    $[107] = t55;
  } else {
    t55 = $[107];
  }
  let t56;
  if ($[109] !== t55) {
    t56 = <tbody className="*:data-row:h-9">{t55}</tbody>;
    $[109] = t55;
    $[110] = t56;
  } else {
    t56 = $[110];
  }
  let t57;
  if ($[111] !== t54 || $[112] !== t56) {
    t57 = <table className="relative border-separate border-spacing-0">{t54}{t56}</table>;
    $[111] = t54;
    $[112] = t56;
    $[113] = t57;
  } else {
    t57 = $[113];
  }
  let t58;
  if ($[114] !== table) {
    t58 = <TableGoToLastPage table={table} />;
    $[114] = table;
    $[115] = t58;
  } else {
    t58 = $[115];
  }
  let t59;
  if ($[116] !== t57 || $[117] !== t58) {
    t59 = <div ref={rootRef} className="grow overflow-auto overflow-y-scroll overscroll-contain [scrollbar-width:thin]">{t57}{t58}</div>;
    $[116] = t57;
    $[117] = t58;
    $[118] = t59;
  } else {
    t59 = $[118];
  }
  let t60;
  if ($[119] !== items || $[120] !== table) {
    t60 = <TablePagination items={items} table={table} rootRef={rootRef} />;
    $[119] = items;
    $[120] = table;
    $[121] = t60;
  } else {
    t60 = $[121];
  }
  let t61;
  if ($[122] !== table || $[123] !== tableState.pagination.pageSize) {
    t61 = <TablePaginationSizeSelect table={table} sizes={PAGE_SIZES} value={tableState.pagination.pageSize} />;
    $[122] = table;
    $[123] = tableState.pagination.pageSize;
    $[124] = t61;
  } else {
    t61 = $[124];
  }
  const t62 = `/${t("page")}`;
  let t63;
  if ($[125] !== t62) {
    t63 = <div className="px-1 whitespace-nowrap">{t62}</div>;
    $[125] = t62;
    $[126] = t63;
  } else {
    t63 = $[126];
  }
  let t64;
  if ($[127] !== t61 || $[128] !== t63) {
    t64 = <div className="flex grow items-center justify-end"><div className="flex items-center text-xs">{t61}{t63}</div></div>;
    $[127] = t61;
    $[128] = t63;
    $[129] = t64;
  } else {
    t64 = $[129];
  }
  let t65;
  if ($[130] !== t60 || $[131] !== t64) {
    t65 = <div className="flex flex-wrap items-center justify-between gap-0.5 border-t border-t-zinc-200 py-1 pr-0.5 tracking-[.03em] tabular-nums dark:border-neutral-800">{t60}{t64}</div>;
    $[130] = t60;
    $[131] = t64;
    $[132] = t65;
  } else {
    t65 = $[132];
  }
  let t66;
  if ($[133] !== rowsFiltered.length) {
    t66 = <NumberFlow value={rowsFiltered.length} locales="en-US" animated={true} isolate={true} />;
    $[133] = rowsFiltered.length;
    $[134] = t66;
  } else {
    t66 = $[134];
  }
  let t67;
  if ($[135] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = <span>{" items"}</span>;
    $[135] = t67;
  } else {
    t67 = $[135];
  }
  let t68;
  if ($[136] !== t66) {
    t68 = <div className="flex justify-center border-t border-solid border-t-zinc-200 bg-background dark:border-neutral-800"><div className="flex h-7 items-center text-xs tracking-[.03em] tabular-nums"><span>{t66}{t67}</span></div></div>;
    $[136] = t66;
    $[137] = t68;
  } else {
    t68 = $[137];
  }
  let t69;
  if ($[138] !== t52 || $[139] !== t59 || $[140] !== t65 || $[141] !== t68) {
    t69 = <div className="flex grow items-center justify-center overflow-hidden rounded-xl border sq:rounded-3xl"><div className="flex size-full flex-col">{t52}{t59}{t65}{t68}</div></div>;
    $[138] = t52;
    $[139] = t59;
    $[140] = t65;
    $[141] = t68;
    $[142] = t69;
  } else {
    t69 = $[142];
  }
  let t70;
  if ($[143] !== t30 || $[144] !== t69) {
    t70 = <div className="flex h-full flex-col">{t30}{t69}</div>;
    $[143] = t30;
    $[144] = t69;
    $[145] = t70;
  } else {
    t70 = $[145];
  }
  return t70;
}
function _temp29(headerGroup) {
  return <tr key={headerGroup.id}>{headerGroup.headers.map(_temp28)}</tr>;
}
function _temp28(header) {
  return <TableHeaderCellRender key={header.id} header={header} />;
}
function _temp27(language_2) {
  return <SelectItem key={language_2.language_code} className="pr-4 text-xs" value={language_2.language_code}>{language_2.language_name}</SelectItem>;
}
function _temp26(t0) {
  const {
    tableState: tableState_0
  } = t0;
  tableState_0.rowSelection = {};
}
function _temp25(f_0) {
  return f_0.text;
}
function _temp24(f) {
  return f.file.file_name;
}
function _temp23() {
  return true;
}
function _temp22(row) {
  return String(row.id);
}
function _temp21(t0) {
  const {
    media_type
  } = t0;
  return media_type === "tv" || media_type === "movie";
}
function _temp20(language_0) {
  return {
    label: language_0.language_name,
    value: language_0.language_code
  };
}
function _temp19(a, b) {
  return a.language_name.localeCompare(b.language_name);
}
function _temp18(language) {
  return language.language_code;
}
function _temp17(prev_0) {
  return prev_0;
}
function _temp16(t0) {
  const {
    data
  } = t0;
  return data;
}
function _temp15(prev) {
  return prev;
}